import React from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import useAuth from '../../hooks/useAuth';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info } from 'lucide-react';

const SubscriptionLimitBanner = ({ actionType, showIfUnlimited = false }) => {
  const { usage, getCurrentPlan } = useSubscription();
  const { userProfile } = useAuth();

  // Only show for employers
  if (userProfile?.role !== 'employer') {
    return null;
  }

  if (!usage) {
    return null;
  }

  const plan = getCurrentPlan();
  if (!plan) {
    return null;
  }

  const features = plan.features;
  
  let limitValue, currentUsage, remaining, actionName;
  
  switch (actionType) {
    case 'jobs':
      limitValue = features.active_job_posts;
      currentUsage = usage.active_jobs_count;
      actionName = 'job posts';
      break;
    case 'hires':
      limitValue = features.hires_per_month;
      currentUsage = usage.hires_count;
      actionName = 'hires & communications';
      break;
    case 'communications':
      limitValue = features.hires_per_month; // Using same limit as hires
      currentUsage = usage.communications_count;
      actionName = 'communications';
      break;
    default:
      return null;
  }

  // Handle unlimited plans
  if (limitValue === -1) {
    if (!showIfUnlimited) return null;
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex">
          <Info className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Unlimited {actionName}
            </h3>
            <p className="text-sm text-green-700 mt-1">
              Your {plan.name} plan includes unlimited {actionName}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  remaining = limitValue - currentUsage;
  const isNearLimit = remaining <= Math.ceil(limitValue * 0.2); // Within 20% of limit
  const isAtLimit = remaining <= 0;

  if (!isNearLimit && !showIfUnlimited) {
    return null;
  }

  const bgColor = isAtLimit ? 'bg-red-50 border-red-200' : isNearLimit ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200';
  const iconColor = isAtLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-blue-400';
  const textColor = isAtLimit ? 'text-red-800' : isNearLimit ? 'text-yellow-800' : 'text-blue-800';
  const descColor = isAtLimit ? 'text-red-700' : isNearLimit ? 'text-yellow-700' : 'text-blue-700';

  return (
    <div className={`${bgColor} border rounded-lg p-4 mb-6`}>
      <div className="flex">
        <AlertTriangle className={`h-5 w-5 ${iconColor} flex-shrink-0 mt-0.5`} />
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${textColor}`}>
            {isAtLimit ? `${actionName.charAt(0).toUpperCase() + actionName.slice(1)} limit reached` : `${actionName.charAt(0).toUpperCase() + actionName.slice(1)} limit warning`}
          </h3>
          <p className={`text-sm ${descColor} mt-1`}>
            {isAtLimit 
              ? `You've used all ${limitValue} ${actionName} for this month. Upgrade to continue.`
              : `You have ${remaining} of ${limitValue} ${actionName} remaining this month.`
            }
          </p>
          {(isAtLimit || isNearLimit) && (
            <div className="mt-3">
              <Link
                to="/pricing"
                className="text-sm font-medium text-primary hover:text-primary-dark"
              >
                Upgrade your plan →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionLimitBanner;